import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  VStack,
  HStack,
  Text,
  Button,
  Heading,
  Divider,
  Box,
} from '@chakra-ui/react';
import { onboardingService } from '@/services/onboardingService';
import { SupportedLanguage, getAvailableLanguages } from '@/i18n/i18nCore';
import { useLocale } from '@/i18n/useLocale';
import { LocalePicker } from '@/components/ui/LocalePicker';
import * as userPreferencesService from '@/services/userPreferences';
import { ProfileSection } from '@/features/account/components/ProfileSection';

interface SettingsItemProps {
  title: string;
  description: string;
  action: React.ReactNode;
}

function SettingsItem({ title, description, action }: SettingsItemProps) {
  return (
    <HStack
      justify="space-between"
      align="center"
      py={3}
      px={4}
      borderRadius="md"
      bg="gray.50"
    >
      <VStack flex={1} mr={4} align="stretch" spacing={1}>
        <Text fontSize="md" fontWeight="bold" color="gray.800">
          {title}
        </Text>
        <Text fontSize="sm" color="gray.600">
          {description}
        </Text>
      </VStack>
      {action}
    </HStack>
  );
}

export function SettingsPage() {
  const navigate = useNavigate();
  const [isResettingOnboarding, setIsResettingOnboarding] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const prefsAdapter = useMemo(
    () => ({
      getPreferences: userPreferencesService.getPreferences,
      updatePreferences: userPreferencesService.updatePreferences,
    }),
    [],
  );
  const { locale, setLocale, isSyncing, isLoading: localeLoading } = useLocale(prefsAdapter, true);
  const availableLanguages = useMemo(() => {
    const langs = getAvailableLanguages();
    const flags: Record<string, string> = { en: '🇬🇧', uk: '🇺🇦' };
    return langs.map((l: { code: string; nativeName: string }) => ({
      code: l.code,
      label: l.nativeName,
      flag: flags[l.code] || '🌐',
    }));
  }, []);

  const handleReplayOnboarding = useCallback(async () => {
    setIsResettingOnboarding(true);
    setSuccessMessage(null);
    setError(null);
    try {
      await onboardingService.reset();
      setSuccessMessage('Onboarding has been reset. Redirecting...');
      setTimeout(() => navigate('/onboarding'), 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset onboarding');
    } finally {
      setIsResettingOnboarding(false);
    }
  }, [navigate]);

  return (
    <VStack flex={1} p={4} maxW="800px" mx="auto" w="100%" align="stretch" spacing={6}>
      <Heading size="lg" mb={4}>Settings</Heading>

      <ProfileSection />

      {/* Language Section */}
      <VStack align="stretch" spacing={3}>
        <Text fontSize="lg" fontWeight="bold" color="gray.800">Language</Text>
        <Divider />
        <Box p={4} bg="gray.50" borderRadius="md">
          <Text fontSize="md" fontWeight="bold" color="gray.800" mb={2}>
            Display Language
          </Text>
          <Text fontSize="sm" color="gray.600" mb={3}>
            Choose your preferred language for the interface
          </Text>
          <LocalePicker
            currentLocale={locale}
            availableLanguages={availableLanguages}
            onLocaleChange={(code) => setLocale(code as SupportedLanguage)}
            isSyncing={isSyncing}
            isLoading={localeLoading}
          />
        </Box>
      </VStack>

      {/* Help Section */}
      <VStack align="stretch" spacing={3}>
        <Text fontSize="lg" fontWeight="bold" color="gray.800">Help & Support</Text>
        <Divider />
        <VStack spacing={3}>
          <SettingsItem
            title="Replay Onboarding"
            description="View the onboarding tutorial again to learn about Streamko's features"
            action={
              <Button
                onClick={handleReplayOnboarding}
                isDisabled={isResettingOnboarding}
                colorScheme="blue"
                size="sm"
              >
                {isResettingOnboarding ? 'Resetting...' : 'Replay'}
              </Button>
            }
          />
        </VStack>
        {successMessage && (
          <Text color="green.500" mt={3} textAlign="center">{successMessage}</Text>
        )}
        {error && (
          <Text color="red.500" mt={3} textAlign="center">{error}</Text>
        )}
      </VStack>

      {/* About Section */}
      <VStack align="stretch" spacing={3}>
        <Text fontSize="lg" fontWeight="bold" color="gray.800">About</Text>
        <Divider />
        <Box p={4} bg="gray.50" borderRadius="md">
          <Text fontSize="md" fontWeight="bold" color="gray.800">Streamko</Text>
          <Text fontSize="sm" color="gray.600" mt={1}>Version 1.0.0</Text>
          <Text fontSize="sm" color="gray.600" mt={2}>
            Digital signage made simple. Manage your content, playlists, and displays from anywhere.
          </Text>
        </Box>
      </VStack>
    </VStack>
  );
}
